/**
 * Human-readable sitemap tree page.
 * The XML sitemap remains available at /sitemap.xml for search engines.
 */

import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { getSitemapRouteTree, type SitemapNode } from '@/lib/seoManager';

function TreeList({ nodes }: { nodes: SitemapNode[] }) {
  return (
    <ul className="space-y-2">
      {nodes.map((node) => (
        <li key={node.path} className="text-white/90">
          <Link to={node.path} className="hover:text-white transition-colors">
            {node.title}
          </Link>{' '}
          <span className="text-white/40 text-xs">{node.path}</span>
          {node.children && node.children.length > 0 && (
            <div className="ml-5 mt-2 border-l border-white/10 pl-4">
              <TreeList nodes={node.children} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export const SitemapPage = () => {
  const routeTree = getSitemapRouteTree();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Rooflyzer Sitemap',
    description: 'Tree-structured sitemap of all indexable Rooflyzer pages.',
    url: 'https://rooflyzer.pro/sitemap',
  };

  return (
    <>
      <SEOHead
        title="Sitemap"
        description="Browse every indexable Rooflyzer page. XML sitemap is available for Google Search Console submission."
        canonicalUrl="/sitemap"
        schema={schema}
      />

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Sitemap</h1>
          <p className="text-white/80 mb-8">
            This page shows the full page tree. For search engines, submit the XML sitemap:
            <a
              className="ml-2 text-emerald-400 hover:underline"
              href="https://rooflyzer.pro/sitemap.xml"
            >
              https://rooflyzer.pro/sitemap.xml
            </a>
          </p>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <TreeList nodes={routeTree} />
          </div>
        </div>
      </div>
    </>
  );
};

export default SitemapPage;
